'use client';

import React from 'react';
import {
  LuLayoutDashboard,
  LuPackage,
  LuTruck,
  LuUsers,
  LuSettings,
  LuCar,
  LuPlus,
  LuSearch,
  LuBell,
  LuMapPin, // ✅ NEW for Tracking
} from 'react-icons/lu';

import {
  Box,
  VStack,
  HStack,
  Button,
  Badge,
  Portal,
  Menu,
  useBreakpointValue,
  Text,
} from '../index';

import { NavigationItem } from './navigation-config-mvp';

export interface MobileNavigationBarProps {
  pathname?: string;
  onNavigate?: (href: string) => void;
  onQuickAction?: (action: string) => void;
  showQuickActions?: boolean;
  notificationCount?: number;
}

const mvpNavigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    href: '/dashboard',
    icon: LuLayoutDashboard,
  },
  {
    id: 'orders',
    label: 'Orders',
    href: '/orders',
    icon: LuPackage,
    badge: {
      content: 'New',
      colorPalette: 'red',
    },
  },
  {
    id: 'shipments',
    label: 'Shipments',
    href: '/shipments',
    icon: LuTruck,
  },
  // ✅ NEW: Tracking Module (Phase 1)
  {
    id: 'tracking',
    label: 'Tracking',
    href: '/tracking',
    icon: LuMapPin,
    badge: {
      content: 0, // Will be dynamic in Phase 5
      colorPalette: 'green',
    },
  },
  {
    id: 'customers',
    label: 'Customers',
    href: '/customers',
    icon: LuUsers,
  },
  {
    id: 'settings',
    label: 'Settings',
    href: '/settings',
    icon: LuSettings,
  },
  {
    id: 'fleet',
    label: 'Fleet',
    href: '/fleet/vehicles',
    icon: LuCar,
  },
];

export function MobileNavigationBar({
  pathname = '',
  onNavigate,
  onQuickAction,
  showQuickActions = true,
  notificationCount = 0,
}: MobileNavigationBarProps) {
  const isMobile = useBreakpointValue({ base: true, md: false });

  if (!isMobile) {
    return null;
  }

  const handleNavigate = (href: string) => {
    if (onNavigate) {
      onNavigate(href);
    }
  };

  const handleQuickAction = (action: string) => {
    if (onQuickAction) {
      onQuickAction(action);
    }
  };

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      backgroundColor="bg.canvas"
      borderTopWidth="1px"
      borderColor="border.muted"
      zIndex="docked"
      padding="safe"
    >
      {/* Main Navigation */}
      <HStack paddingX={2} paddingY={1} gap={1}>
        {mvpNavigationItems.map((item) => {
          const isActive = item.href === pathname;
          const Icon = item.icon;

          return (
            <Button
              key={item.id}
              variant={isActive ? 'primary' : 'ghost'}
              colorPalette={isActive ? 'blue' : 'gray'}
              fullWidth
              size="lg"
              onClick={() => item.href && handleNavigate(item.href)}
            >
              <HStack gap={2} width="full" justifyContent="space-between">
                <VStack gap={0.5}>
                  {Icon && <Icon size={20} />}
                  <Text fontSize="xs" fontWeight="medium">
                    {item.label}
                  </Text>
                </VStack>
                {item.badge && (
                  <Badge
                    size="sm"
                    colorPalette={
                      (item.badge.colorPalette || 'gray') as
                        | 'gray'
                        | 'red'
                        | 'green'
                        | 'blue'
                        | 'orange'
                        | 'yellow'
                        | 'purple'
                        | 'brand'
                    }
                  >
                    {item.badge.content}
                  </Badge>
                )}
              </HStack>
            </Button>
          );
        })}

        {/* Quick Actions */}
        {showQuickActions && (
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button variant="ghost" colorPalette="blue" fullWidth size="lg">
                <VStack gap={0.5} width="full">
                  <LuPlus size={20} />
                  <Text fontSize="xs" fontWeight="medium">
                    More
                  </Text>
                </VStack>
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.Item onSelect={() => handleQuickAction('new-order')}>
                    <HStack gap={2}>
                      <LuPackage size={16} />
                      <Text>New Order</Text>
                    </HStack>
                  </Menu.Item>
                  <Menu.Item onSelect={() => handleQuickAction('new-shipment')}>
                    <HStack gap={2}>
                      <LuTruck size={16} />
                      <Text>New Shipment</Text>
                    </HStack>
                  </Menu.Item>
                  <Menu.Item onSelect={() => handleQuickAction('search')}>
                    <HStack gap={2}>
                      <LuSearch size={16} />
                      <Text>Search</Text>
                    </HStack>
                  </Menu.Item>
                  <Menu.Separator />
                  <Menu.Item onSelect={() => handleQuickAction('notifications')}>
                    <HStack gap={2} justifyContent="space-between" width="full">
                      <HStack gap={2}>
                        <LuBell size={16} />
                        <Text>Notifications</Text>
                      </HStack>
                      {notificationCount > 0 && (
                        <Badge size="sm" colorPalette="red">
                          {notificationCount}
                        </Badge>
                      )}
                    </HStack>
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        )}
      </HStack>
    </Box>
  );
}
